import { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { Trash2, Edit3, Save, X, Plus } from 'lucide-react';

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

// ─── Animations ────────────────────────────────────────────────────────────────
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// ─── Layout ────────────────────────────────────────────────────────────────────
const PageWrapper = styled.div`
  width: 100%;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

// ─── Header ────────────────────────────────────────────────────────────────────
const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: ${({ disabled }) => (disabled ? '#9ca3af' : '#2563eb')};
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #1d4ed8;
  }
`;

// ─── Divider ───────────────────────────────────────────────────────────────────
const Divider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin-bottom: 16px;
`;

// ─── Error Banner ──────────────────────────────────────────────────────────────
const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #dc2626;
  font-weight: 500;
`;

// ─── Accordion ─────────────────────────────────────────────────────────────────
const AccordionList = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
`;

const AccordionItem = styled.div`
  border-bottom: 1px solid #e2e8f0;
  &:last-child {
    border-bottom: none;
  }
`;

const AccordionHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: ${({ open }) => (open ? '#eff6ff' : '#ffffff')};
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;

  &:hover {
    background: ${({ open }) => (open ? '#eff6ff' : '#f8fafc')};
  }
`;

const AccordionLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const IndexDot = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${({ open }) => (open ? '#2563eb' : '#e2e8f0')};
  color: ${({ open }) => (open ? '#ffffff' : '#64748b')};
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
`;

const HeadingText = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${({ open }) => (open ? '#2563eb' : '#1e293b')};
  transition: color 0.15s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const IconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  color: ${({ color, disabled }) => (disabled ? '#9ca3af' : color || '#374151')};
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: ${({ hoverBg }) => hoverBg || '#f1f5f9'};
  }
`;

const ChevronIcon = styled.span`
  font-size: 11px;
  color: ${({ open }) => (open ? '#2563eb' : '#94a3b8')};
  transition: transform 0.2s, color 0.15s;
  transform: rotate(${({ open }) => (open ? '180deg' : '0deg')});
  flex-shrink: 0;
`;

const AccordionBody = styled.div`
  max-height: ${({ open }) => (open ? '1200px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const AccordionContent = styled.div`
  padding: 16px;
  background: #fafafa;
  border-top: 1px solid #e2e8f0;
`;

// ─── View Mode: Two-column ──────────────────────────────────────────────────────
const ViewColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
`;

const ColumnLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const DescriptionContent = styled.div`
  font-size: 18px;
  color: #374151;
  line-height: 1.75;

  ul, ol { padding-left: 20px; margin: 6px 0; }
  li { margin-bottom: 4px; }
  p { margin: 0 0 6px; }
  p:last-child { margin-bottom: 0; }
`;

// ─── Edit Mode ─────────────────────────────────────────────────────────────────
const EditColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 16px;
`;

const EditColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const HeadingInput = styled.input`
  width: 100%;
  padding: 7px 11px;
  font-size: 14px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  outline: none;
  transition: border 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 1px #3b82f6;
  }
`;

const EditActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const SaveBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  background: ${({ disabled }) => (disabled ? '#9ca3af' : '#16a34a')};
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  border: none;
  border-radius: 5px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #15803d;
  }
`;

const CancelBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  background: ${({ disabled }) => (disabled ? '#e5e7eb' : '#f3f4f6')};
  color: #374151;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #e5e7eb;
  }
`;

// ─── State Views ───────────────────────────────────────────────────────────────
const CenterBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  gap: 10px;
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid #e2e8f0;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: ${spin} 0.75s linear infinite;
`;

const StateText = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 0;
  font-weight: 500;
`;

const RefreshBar = styled.div`
  text-align: center;
  padding: 8px;
  font-size: 12px;
  color: #2563eb;
  font-weight: 600;
`;

// ─── EditablePointsList ─────────────────────────────────────────────────────────
const EditablePointsList = ({ language, points, onPointChange, onAddPoint, onDeletePoint }) => (
  <div style={{ marginBottom: '8px' }}>
    {points.map((point, pointIndex) => (
      <div key={`${language}-${pointIndex}`} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ color: '#9CA3AF', marginRight: '8px' }}>•</span>
        <input
          type="text"
          value={point}
          onChange={(e) => onPointChange(language, pointIndex, e.target.value)}
          style={{
            flex: 1,
            padding: '6px 12px',
            fontSize: '14px',
            border: '1px solid #D1D5DB',
            borderRadius: '4px',
            outline: 'none',
            transition: 'all 0.2s',
          }}
          placeholder={`Point ${pointIndex + 1}`}
          onFocus={(e) => {
            e.target.style.border = '1px solid #3B82F6';
            e.target.style.boxShadow = '0 0 0 1px #3B82F6';
          }}
          onBlur={(e) => {
            e.target.style.border = '1px solid #D1D5DB';
            e.target.style.boxShadow = 'none';
          }}
        />
        <button
          onClick={() => onDeletePoint(language, pointIndex)}
          style={{
            padding: '6px',
            color: '#EF4444',
            background: 'transparent',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'background 0.2s',
          }}
          onMouseOver={(e) => (e.currentTarget.style.background = '#FEF2F2')}
          onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          <Trash2 size={14} />
        </button>
      </div>
    ))}
    <button
      onClick={() => onAddPoint(language)}
      style={{
        fontSize: '14px',
        color: '#2563EB',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontWeight: '500',
      }}
      onMouseOver={(e) => (e.currentTarget.style.color = '#1D4ED8')}
      onMouseOut={(e) => (e.currentTarget.style.color = '#2563EB')}
    >
      + Add Point
    </button>
  </div>
);

// ─── Main Component ─────────────────────────────────────────────────────────────
const Advisory = () => {
  const [advisories, setAdvisories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingAdvisoryId, setEditingAdvisoryId] = useState(null);
  const [editableEnglishHeading, setEditableEnglishHeading] = useState('');
  const [editableEnglishPoints, setEditableEnglishPoints] = useState([]);
  const [editableTetumHeading, setEditableTetumHeading] = useState('');
  const [editableTetumPoints, setEditableTetumPoints] = useState([]);
  const [openIndex, setOpenIndex] = useState(0);

  const fetchAdvisories = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${backendUrl}/EarthQuake/earthquake-advisories/`);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}. Response: ${errorText.substring(0, 200)}`);
      }
      const data = await response.json();
      setAdvisories(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to fetch advisories:', e);
      setError('Failed to load advisories. Please try again later.');
      setAdvisories([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdvisories();
  }, [fetchAdvisories]);

  const parseDescriptionToList = (htmlString) => {
    if (!htmlString) return [];
    const matches = [...htmlString.matchAll(/<li>(.*?)<\/li>/g)];
    return matches.map((match) => match[1]);
  };

  const listToDescriptionHtml = (itemsArray) => {
    if (!itemsArray || itemsArray.length === 0) return '<ul></ul>';
    return `<ul>${itemsArray.map((item) => `<li>${item}</li>`).join('')}</ul>`;
  };

  const handleEdit = (advisory, index) => {
    setEditingAdvisoryId(advisory.id);
    setEditableEnglishHeading(advisory.englishheading || '');
    setEditableEnglishPoints(parseDescriptionToList(advisory.englishdescription || ''));
    setEditableTetumHeading(advisory.tetumheading || '');
    setEditableTetumPoints(parseDescriptionToList(advisory.tetumdescription || ''));
    setOpenIndex(index);
  };

  const handleCancelEdit = () => {
    setEditingAdvisoryId(null);
  };

  const handleSaveEdit = async () => {
    if (editingAdvisoryId === null) return;

    const updatedEnglishDescriptionHtml = listToDescriptionHtml(editableEnglishPoints);
    const updatedTetumDescriptionHtml = listToDescriptionHtml(editableTetumPoints);

    const payload = {
      englishheading: editableEnglishHeading,
      englishdescription: updatedEnglishDescriptionHtml,
      tetumheading: editableTetumHeading,
      tetumdescription: updatedTetumDescriptionHtml,
    };

    setIsLoading(true);
    try {
      const response = await fetch(`${backendUrl}/EarthQuake/earthquake-advisories/${editingAdvisoryId}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorDataText = 'Error details not available in JSON format.';
        try {
          const errorData = await response.json();
          errorDataText = JSON.stringify(errorData);
        } catch (jsonError) {
          errorDataText = await response.text();
        }
        throw new Error(`Failed to save advisory (ID: ${editingAdvisoryId}): ${response.status} - ${errorDataText}`);
      }
      handleCancelEdit();
      fetchAdvisories();
    } catch (e) {
      console.error('Error saving advisory:', e);
      setError(`Failed to save changes for advisory ID ${editingAdvisoryId}: ${e.message}`);
      setIsLoading(false);
    }
  };

  const handlePointChange = (language, index, value) => {
    if (language === 'english') {
      const newPoints = [...editableEnglishPoints];
      newPoints[index] = value;
      setEditableEnglishPoints(newPoints);
    } else if (language === 'tetum') {
      const newPoints = [...editableTetumPoints];
      newPoints[index] = value;
      setEditableTetumPoints(newPoints);
    }
  };

  const handleAddPointToList = (language) => {
    if (language === 'english') {
      setEditableEnglishPoints([...editableEnglishPoints, '']);
    } else if (language === 'tetum') {
      setEditableTetumPoints([...editableTetumPoints, '']);
    }
  };

  const handleDeletePoint = (language, index) => {
    if (language === 'english') {
      setEditableEnglishPoints(editableEnglishPoints.filter((_, i) => i !== index));
    } else if (language === 'tetum') {
      setEditableTetumPoints(editableTetumPoints.filter((_, i) => i !== index));
    }
  };

  const handleAddNewAdvisory = async () => {
    const newAdvisoryData = {
      englishheading: 'Earthquake Advisory',
      englishdescription: '<ul><li>Point 1</li></ul>',
      tetumheading: 'Avizu Terremotu',
      tetumdescription: '<ul><li>Foun 1</li></ul>',
    };

    setIsLoading(true);
    try {
      const response = await fetch(`${backendUrl}/earthquake/earthquake-advisories/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newAdvisoryData),
      });
      if (!response.ok) {
        let errorDataText = 'Error details not available in JSON format.';
        try {
          const errorData = await response.json();
          errorDataText = JSON.stringify(errorData);
        } catch (jsonError) {
          errorDataText = await response.text();
        }
        throw new Error(`Failed to add new advisory: ${response.status} - ${errorDataText}`);
      }
      fetchAdvisories();
    } catch (e) {
      console.error('Error adding new advisory:', e);
      setError(`Failed to add new advisory: ${e.message}`);
      setIsLoading(false);
    }
  };

  const handleDeleteAdvisory = async (advisoryId) => {
    if (!window.confirm('Are you sure you want to delete this advisory section? This action cannot be undone.')) {
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${backendUrl}/earthquake/earthquake-advisories/${advisoryId}/`, {
        method: 'DELETE',
        headers: {},
      });
      if (!response.ok && response.status !== 204) {
        let errorDataText = 'Error details not available.';
        try {
          const errorData = await response.json();
          errorDataText = JSON.stringify(errorData);
        } catch (jsonError) {
          errorDataText = await response.text();
        }
        throw new Error(`Failed to delete advisory (ID: ${advisoryId}): ${response.status} - ${errorDataText}`);
      }
      fetchAdvisories();
    } catch (e) {
      console.error('Error deleting advisory:', e);
      setError(`Failed to delete advisory (ID: ${advisoryId}): ${e.message}`);
      setIsLoading(false);
    }
  };

  // ─── Loading / Error full-page states ────────────────────────────────────────
  if (isLoading && advisories.length === 0 && !error) {
    return (
      <PageWrapper>
        <CenterBox>
          <Spinner />
          <StateText>Loading advisories...</StateText>
        </CenterBox>
      </PageWrapper>
    );
  }

  if (error && advisories.length === 0) {
    return (
      <PageWrapper>
        <CenterBox>
          <StateText style={{ color: '#dc2626' }}>{error}</StateText>
        </CenterBox>
      </PageWrapper>
    );
  }

  const isActionsDisabled = isLoading || editingAdvisoryId !== null;

  return (
    <PageWrapper>
      {/* Page Header */}
      <HeaderRow>
        <Title>Earthquake Advisory</Title>
        <AddButton
          onClick={handleAddNewAdvisory}
          disabled={isActionsDisabled}
        >
          <Plus size={15} />
          Add New Section
        </AddButton>
      </HeaderRow>

      <Divider />

      {/* Inline error banner (when data already loaded) */}
      {error && advisories.length > 0 && (
        <ErrorBanner>⚠ {error}</ErrorBanner>
      )}

      {/* Accordion list */}
      {advisories.length > 0 ? (
        <AccordionList>
          {advisories.map((advisory, index) => {
            const isOpen = openIndex === index;
            const isEditing = editingAdvisoryId === advisory.id;

            return (
              <AccordionItem key={advisory.id}>
                {/* ── Header ── */}
                <AccordionHeader
                  open={isOpen}
                  onClick={() => {
                    if (!isEditing) {
                      setOpenIndex(isOpen ? null : index);
                    }
                  }}
                >
                  <AccordionLeft>
                    <IndexDot open={isOpen}>{index + 1}</IndexDot>
                    <HeadingText open={isOpen}>
                      {advisory.englishheading} / {advisory.tetumheading}
                    </HeadingText>
                  </AccordionLeft>

                  {/* Action buttons — stop propagation so clicks don't toggle accordion */}
                  <ActionButtons onClick={(e) => e.stopPropagation()}>
                    <IconBtn
                      title="Edit"
                      disabled={isActionsDisabled}
                      color="#2563eb"
                      hoverBg="#eff6ff"
                      onClick={() => handleEdit(advisory, index)}
                    >
                      <Edit3 size={15} />
                    </IconBtn>
                    <IconBtn
                      title="Delete"
                      disabled={isActionsDisabled}
                      color="#ef4444"
                      hoverBg="#fef2f2"
                      onClick={() => handleDeleteAdvisory(advisory.id)}
                    >
                      <Trash2 size={15} />
                    </IconBtn>
                  </ActionButtons>

                  <ChevronIcon open={isOpen}>▼</ChevronIcon>
                </AccordionHeader>

                {/* ── Body ── */}
                <AccordionBody open={isOpen}>
                  <AccordionContent>
                    {isEditing ? (
                      /* Edit mode */
                      <>
                        <EditColumns>
                          <EditColumn>
                            <ColumnLabel>English</ColumnLabel>
                            <HeadingInput
                              type="text"
                              value={editableEnglishHeading}
                              onChange={(e) => setEditableEnglishHeading(e.target.value)}
                              placeholder="English heading"
                            />
                            <EditablePointsList
                              language="english"
                              points={editableEnglishPoints}
                              onPointChange={handlePointChange}
                              onAddPoint={handleAddPointToList}
                              onDeletePoint={handleDeletePoint}
                            />
                          </EditColumn>
                          <EditColumn>
                            <ColumnLabel>Tetum</ColumnLabel>
                            <HeadingInput
                              type="text"
                              value={editableTetumHeading}
                              onChange={(e) => setEditableTetumHeading(e.target.value)}
                              placeholder="Tetum heading"
                            />
                            <EditablePointsList
                              language="tetum"
                              points={editableTetumPoints}
                              onPointChange={handlePointChange}
                              onAddPoint={handleAddPointToList}
                              onDeletePoint={handleDeletePoint}
                            />
                          </EditColumn>
                        </EditColumns>
                        <EditActions>
                          <SaveBtn onClick={handleSaveEdit} disabled={isLoading}>
                            <Save size={14} />
                            Save
                          </SaveBtn>
                          <CancelBtn onClick={handleCancelEdit} disabled={isLoading}>
                            <X size={14} />
                            Cancel
                          </CancelBtn>
                        </EditActions>
                      </>
                    ) : (
                      /* View mode */
                      <ViewColumns>
                        <div>
                          <ColumnLabel>English</ColumnLabel>
                          <DescriptionContent
                            dangerouslySetInnerHTML={{
                              __html: advisory.englishdescription || '<ul></ul>',
                            }}
                          />
                        </div>
                        <div>
                          <ColumnLabel>Tetum</ColumnLabel>
                          <DescriptionContent
                            dangerouslySetInnerHTML={{
                              __html: advisory.tetumdescription || '<ul></ul>',
                            }}
                          />
                        </div>
                      </ViewColumns>
                    )}
                  </AccordionContent>
                </AccordionBody>
              </AccordionItem>
            );
          })}
        </AccordionList>
      ) : (
        <CenterBox>
          <StateText>No earthquake advisories available.</StateText>
          <AddButton onClick={handleAddNewAdvisory} disabled={isActionsDisabled}>
            <Plus size={15} />
            Add New Section
          </AddButton>
        </CenterBox>
      )}

      {isLoading && advisories.length > 0 && (
        <RefreshBar>Processing...</RefreshBar>
      )}
    </PageWrapper>
  );
};

export default Advisory;
